import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Megaphone, Menu, SlidersHorizontal, Tags, UserRoundPlus } from 'lucide-react';
import { useHasPermission } from '@shared/lib/permissions';
import { env } from '@config/env';
import { useHomeLayoutStore, resolveWidgets } from '@shared/stores/homeLayoutStore';
import { WIDGET_REGISTRY } from '@features/dashboard/widgets/registry';
import { CustomizeHomeSheet } from '@features/dashboard/CustomizeHomeSheet';
import { tileColSpanClass } from '@shared/lib/tileGrid';
import { NotificationBell } from '@features/notifications/NotificationBell';
import { MoreCategoryTilesSheet } from '@features/dashboard/MoreCategoryTilesSheet';
import { PropertySwitcher } from '@features/dashboard/PropertySwitcher';
import { useAuthStore } from '@shared/stores/authStore';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useUiStore } from '@shared/stores/uiStore';
import { runTileAction, isPanicTile } from '@shared/lib/tileActions';
import { homeTileGridClass } from '@shared/lib/tileGrid';
import { useMemberProfile } from '@shared/hooks/useMemberProfile';
import { memberGreetingName } from '@features/profile/api';
import { QuickTile } from '@features/dashboard/QuickTile';
import { useVisibleTiles, useVisibleCategories } from '@features/dashboard/useVisibleTiles';
import { QuickLinkTile, type QuickLinkItem } from '@features/dashboard/QuickLinkTile';
import { QuickLinksSheet } from '@features/dashboard/QuickLinksSheet';
import {
  useQuickLinksStore,
  orderByRecentUse,
  QUICK_LINKS_VISIBLE,
} from '@shared/stores/quickLinksStore';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { BannerCarousel } from '@features/dashboard/BannerCarousel';
import { useHomeBannerLocations } from '@shared/hooks/useBannerLocation';
import type { CompiledMoreTileCategory } from '@shared/types/domainConfig';

/**
 * Home / dashboard — Fancourt layout: a domain-resolved photo header
 * carrying the greeting and property switcher, over a tile grid driven
 * by the tenant's domain config (`experienceStore.tiles.main`).
 */
export function Home(): JSX.Element {
  const navigate = useNavigate();
  const canMemberRequests = useHasPermission('members:requests:approve');
  // Two independent hooks, combined after both run unconditionally — short-circuiting
  // `&&` across hook calls would make the second call conditional (Rules of Hooks).
  const hasBroadcastPermission = useHasPermission('communication:bulk_communications:publish');
  const communicationModuleEnabled = useModuleStore((s) => s.isModuleEnabled('communication'));
  const canBroadcast = hasBroadcastPermission && communicationModuleEnabled;
  const user = useAuthStore((s) => s.user);
  const profileQuery = useMemberProfile();
  const experience = useExperienceStore((s) => s.experience);
  const moreCategories = useExperienceStore((s) => s.getMoreCategories());
  const openNavDrawer = useUiStore((s) => s.openNavDrawer);
  const [categorySheet, setCategorySheet] = useState<CompiledMoreTileCategory | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const { topBanner, bottomBanner } = useHomeBannerLocations();

  // Widget mode: a horizontal Quick Links rail + configurable widgets below.
  const widgetMode = !!env.home?.widgetMode;
  const widgetOrder = useHomeLayoutStore((s) => s.order);
  const widgetEnabledOverride = useHomeLayoutStore((s) => s.enabledOverride);
  const activeWidgets = useMemo(
    () =>
      resolveWidgets(env.home?.widgets, {
        order: widgetOrder,
        enabledOverride: widgetEnabledOverride,
      }).filter((w) => w.enabled && WIDGET_REGISTRY[w.type]),
    [widgetOrder, widgetEnabledOverride],
  );
  const propertiesModuleEnabled = useModuleStore((s) => s.isModuleEnabled('properties'));

  const greetingName = memberGreetingName(profileQuery.data, user);
  const profileInitial = greetingName.charAt(0).toUpperCase();

  // The backend compiles `tiles.main` per tenant by module, so render it as-is
  // apart from what only the client can know: whether this member's role can
  // open the module, and whether an eForm tile's form is still accepting
  // submissions (see `useVisibleTiles`). (Re-gating on modules client-side
  // wrongly hid every tile: `moduleConfig` only carries settings blocks, not a
  // full enablement map.)
  const allTiles = useMemo(
    () => [...(experience?.tiles?.main ?? [])].sort((a, b) => a.order - b.order),
    [experience],
  );
  const tiles = useVisibleTiles(allTiles);

  // One home-grid tile per `more_categories` entry (member portal parity).
  // Groups left with no reachable tile drop out with their tiles.
  const categoryTiles = useVisibleCategories(moreCategories);

  // Every quick link, flattened from its four sources into one list so the rail
  // and the "View all" sheet stay in sync.
  const quickLinks = useMemo<QuickLinkItem[]>(() => {
    const links: QuickLinkItem[] = tiles.map((tile) => ({
      id: tile.id,
      label: tile.label,
      imageUrl: tile.image_url,
      isPanic: isPanicTile(tile),
      onSelect: () => runTileAction(tile, navigate),
    }));

    for (const category of categoryTiles) {
      links.push({
        id: `category:${category.category_id}`,
        label: category.name,
        fallbackIcon: Tags,
        onSelect: () => setCategorySheet(category),
      });
    }

    // Admin: pending member join requests (members:requests:approve).
    if (canMemberRequests) {
      links.push({
        id: 'member-requests',
        label: 'Member requests',
        fallbackIcon: UserRoundPlus,
        onSelect: () => navigate('/app/member-requests'),
      });
    }

    // Admin: broadcast an SMS/in-app message (communication:bulk_communications:publish).
    if (canBroadcast) {
      links.push({
        id: 'send-message',
        label: 'Send message',
        fallbackIcon: Megaphone,
        onSelect: () => navigate('/app/admin/communication'),
      });
    }

    return links;
  }, [tiles, categoryTiles, canMemberRequests, canBroadcast, navigate]);

  // Most-recently-opened first; the rail shows the head of that list.
  const recentQuickLinks = useQuickLinksStore((s) => s.recent);
  const promoteQuickLink = useQuickLinksStore((s) => s.promote);
  const orderedQuickLinks = useMemo(
    () => orderByRecentUse(quickLinks, recentQuickLinks),
    [quickLinks, recentQuickLinks],
  );
  const railQuickLinks = orderedQuickLinks.slice(0, QUICK_LINKS_VISIBLE);

  const openQuickLink = (link: QuickLinkItem) => {
    promoteQuickLink(link.id);
    link.onSelect();
  };

  return (
    <div className="min-h-full bg-white">
      {/*
        Sticky on the OUTER shell (not the header itself): a sticky child can't
        escape its parent's box, and this wrapper is exactly header-height, so
        sticking the wrapper is what actually pins the header while content
        scrolls under. The wrapper carries the rounded shadow; the header inside
        stays `relative` (sticky={false}) so its bg/overlay clip to the rounded
        corners. z-30 keeps it under modals/sheets/tab-bar (z-50+).
      */}
      <div className="sticky top-0 z-30 overflow-visible rounded-b-3xl shadow-[0_14px_32px_-8px_rgba(0,0,0,0.28)]">
        {/*
          Greeting header. Member name intentionally not shown in the nav bar —
          keep only the menu/property title (leading) and notifications/avatar (action).
        */}
        <HeroHeader
          sticky={false}
          showBack={false}
          eyebrowClassName="mt-0"
          eyebrow={greetingFor(new Date())}
          title=""
          // No centred title here, so give the leading cluster every pixel the
          // bell/avatar don't need — the property title block reads, not truncates.
          topRowClassName="grid-cols-[minmax(0,1fr)_auto_auto]"
          leading={
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={openNavDrawer}
                aria-label="Open menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
              >
                <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
              </button>
              {propertiesModuleEnabled ? <PropertySwitcher /> : null}
            </div>
          }
          action={
            <div className="flex shrink-0 items-center gap-2">
              <NotificationBell variant="header" />
              <button
                type="button"
                aria-label="Profile"
                onClick={() => navigate('/app/profile')}
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-pill bg-brass text-brass-ink"
              >
                {profileQuery.data?.profile_image_url && !avatarFailed ? (
                  <img
                    src={profileQuery.data.profile_image_url}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <span className="text-body-sm font-semibold">{profileInitial}</span>
                )}
              </button>
            </div>
          }
        />
      </div>

      {topBanner ? (
        <section className="bg-white px-5 pt-4">
          <BannerCarousel
            config={topBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      {/* ── Tiles ────────────────────────────────────────────────── */}
      {widgetMode ? (
        <section className="bg-white px-5 pt-6 pb-safe-or-6">
          {/* Quick Links — horizontal scrollable rail, capped at QUICK_LINKS_VISIBLE. */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-body font-bold leading-tight text-ink">Quick Links</h2>
            {orderedQuickLinks.length > railQuickLinks.length ? (
              <button
                type="button"
                onClick={() => setQuickLinksOpen(true)}
                className="flex shrink-0 items-center gap-0.5 text-body-sm font-semibold text-primary-700"
              >
                View all
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
              </button>
            ) : null}
          </div>

          {railQuickLinks.length > 0 ? (
            <div className="-mx-5 -my-4 flex gap-2 overflow-x-auto px-5 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {railQuickLinks.map((link) => (
                <div key={link.id} className="w-[5rem] shrink-0">
                  <QuickLinkTile link={link} onSelect={() => openQuickLink(link)} />
                </div>
              ))}
            </div>
          ) : null}

          {/* Widgets */}
          {activeWidgets.length > 0 ? (
            <div className="mt-5 flex flex-col gap-4">
              {activeWidgets.map((widget) => {
                const def = WIDGET_REGISTRY[widget.type];
                if (!def) return null;
                const Widget = def.component;
                return <Widget key={widget.id} config={widget} />;
              })}
            </div>
          ) : null}

          {/* Customize home — manage which widgets show + their order. */}
          <button
            type="button"
            onClick={() => setCustomizeOpen(true)}
            className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-50 py-3 text-body-sm font-semibold text-primary-700"
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />
            Customize home
          </button>
        </section>
      ) : (
        <section className="bg-white px-5 pt-6 pb-safe-or-6">
          {tiles.length > 0 || categoryTiles.length > 0 ? (
            <ul className={homeTileGridClass()}>
              {tiles.map((tile) => (
                <QuickTile key={tile.id} tile={tile} onSelect={() => runTileAction(tile, navigate)} />
              ))}
              {categoryTiles.map((category) => (
                <MoreCategoryTile
                  key={category.category_id}
                  label={category.name}
                  onSelect={() => setCategorySheet(category)}
                />
              ))}
            </ul>
          ) : (
            <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
              Your community hasn't published any quick links yet.
            </p>
          )}
        </section>
      )}

      {bottomBanner ? (
        <section className="bg-white px-5 pb-safe-or-6">
          <BannerCarousel
            config={bottomBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      <CustomizeHomeSheet open={customizeOpen} onOpenChange={setCustomizeOpen} />

      <QuickLinksSheet
        links={orderedQuickLinks}
        open={quickLinksOpen}
        onOpenChange={setQuickLinksOpen}
        onSelect={(link) => {
          openQuickLink(link);
          setQuickLinksOpen(false);
        }}
      />

      <MoreCategoryTilesSheet
        category={categorySheet}
        open={categorySheet !== null}
        onOpenChange={(open) => {
          if (!open) setCategorySheet(null);
        }}
      />
    </div>
  );
}

function MoreCategoryTile({
  label,
  onSelect,
}: {
  label: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li className={tileColSpanClass(1)}>
      <button type="button" onClick={onSelect} className={homeTileButtonClass}>
        <TileIcon label={label} fallbackIcon={Tags} />
        <TileLabel label={label} />
      </button>
    </li>
  );
}

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 12 && hour >= 5) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
